import React, { PropsWithChildren } from "react";

import { css } from "@compiled/react";
import { useIntl, IntlShape } from "react-intl";

import Lozenge from "@atlaskit/lozenge";

import Link, { LinkProps } from "../../link/src";

function isAbsoluteUrl(url: string) {
    // A URL is considered absolute if it begins with "//" or if it includes "://".
    return /^(\/\/|http(s)?:\/\/)/.test(url);
}

const tiers = (intl: IntlShape) => [
    intl.formatMessage({
        id: "Pricing.features-table.tiers.free",
        defaultMessage: "Free"
    }),
    intl.formatMessage({
        id: "Pricing.features-table.tiers.standard",
        defaultMessage: "Standard"
    }),
    intl.formatMessage({
        id: "Pricing.features-table.tiers.premium",
        defaultMessage: "Premium"
    }),
    intl.formatMessage({
        id: "Pricing.features-table.tiers.enterprise",
        defaultMessage: "Enterprise"
    })
];

const tdStyles = css({
    "borderTop": "1px solid #dfe1e5",
    "padding": "12px 14px",
    "display": "flex",
    "justifyContent": "space-between",
    "fontWeight": 400,
    "@media (min-width: 768px)": {
        display: "table-cell",
        padding: "14px",
        verticalAlign: "middle",
        border: "1px solid #dfe1e5"
    }
});

export const FeaturesTableSection = (props: React.HTMLProps<HTMLElement>) => {
    return (
        <section
            css={css({
                "textAlign": "center",
                "margin": "auto",
                "padding": "16px",
                "maxWidth": "1440px",
                "> a": {
                    margin: "40px 0 64px",
                    fontWeight: 500
                }
            })}
            {...props}
        />
    );
};

export const FeaturesTableHeader = (
    props: React.HTMLProps<HTMLHeadingElement>
) => {
    return (
        // eslint-disable-next-line jsx-a11y/heading-has-content
        <h3
            css={css({
                "color": "#253858",
                "fontSize": "1.25rem",
                "fontWeight": 500,
                "letterSpacing": "0.3px",
                "lineHeight": "1.2",
                "margin": 0,
                "padding": "55px 0 14px",
                "@media (min-width: 768px)": {
                    fontSize: "1.5rem",
                    paddingBlockEnd: "16px"
                }
            })}
            {...props}
        />
    );
};

export const FeaturesTable = (
    props: PropsWithChildren<{ captionText: string }>
) => {
    const intl = useIntl();

    return (
        <table
            css={css({
                display: "flex",
                flexDirection: "column",
                tableLayout: "fixed",
                width: "100%",
                marginBlockEnd: "24px",
                padding: 0,
                border: 0,
                textAlign: "center",
                borderCollapse: "collapse",
                borderSpacing: 0,
                ["@media (min-width: 768px)"]: {
                    display: "table"
                }
            })}
        >
            <caption
                css={css({
                    textAlign: "start",
                    padding: "45px 14px 14px",
                    fontFamily: "var(--bxp-charlie-display-font-family)",
                    fontSize: "1.25rem"
                })}
            >
                {props.captionText}
            </caption>
            <thead
                css={css({
                    display: "none",
                    ["@media (min-width: 768px)"]: {
                        display: "table-header-group"
                    }
                })}
            >
                <tr
                    css={css({
                        "th:first-of-type": {
                            textTransform: "uppercase",
                            textAlign: "start"
                        }
                    })}
                >
                    <FeaturesTableHeadCell>
                        {intl.formatMessage({
                            id: "Pricing.features-table.table-head.feature",
                            defaultMessage: "Feature"
                        })}
                    </FeaturesTableHeadCell>
                    {tiers(intl).map((tier, index) => (
                        <FeaturesTableHeadCell key={index}>
                            {tier}
                        </FeaturesTableHeadCell>
                    ))}
                </tr>
            </thead>
            <tbody>{props.children}</tbody>
        </table>
    );
};

const FeaturesTableHeadCell: React.FC<React.HTMLProps<HTMLTableCellElement>> = (
    props
) => {
    return (
        <th
            css={[tdStyles, css({ backgroundColor: "#fafbfc" })]}
            scope="col"
            {...props}
        />
    );
};

type TierCellContent = boolean | string;

export const FeaturesTableRow = (
    props: PropsWithChildren<{
        featureCellContent: LinkProps | string;
        tierCellsContent: TierCellContent[];
        lozengeText?: string;
    }>
) => {
    const rowOnClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (typeof props.featureCellContent === "string") return;

        if (props.featureCellContent.onClick) {
            props.featureCellContent.onClick(e);
        } else {
            let url = props.featureCellContent.href;
            if (!isAbsoluteUrl(url)) url = `${window.location.origin}${url}`;

            if (props.featureCellContent.type === "external") {
                window.open(url, "_blank");
            }
        }
    };

    let featureCellContent: JSX.Element;
    if (typeof props.featureCellContent === "string") {
        featureCellContent = (
            <span data-feature-content>{props.featureCellContent}</span>
        );
    } else {
        featureCellContent = <Link {...props.featureCellContent} />;
    }

    return (
        <tr
            css={css({
                "cursor": "pointer",
                "display": "flex",
                "flexDirection": "column",
                "margin": "16px 0",
                "border": "1px solid #dfe1e5",
                "backgroundColor": "#fff",
                "transition": "background-color .5s",
                "padding": 0,
                "verticalAlign": "baseline",
                "textAlign": "center",
                "&:hover": {
                    backgroundColor: "#ebebeb"
                },
                ["@media (min-width: 768px)"]: {
                    "display": "table-row",
                    "flexDirection": "row",
                    "&:hover > td:first-of-type": {
                        backgroundColor: "#ebebeb"
                    }
                }
            })}
            onClick={rowOnClick}
        >
            <td
                css={[
                    tdStyles,
                    css({
                        "backgroundColor": "#f4f5f7",
                        "textAlign": "start",
                        "transition": "background-color .5s",
                        "@media (min-width: 768px)": {
                            backgroundColor: "#fff"
                        }
                    })
                ]}
            >
                <span
                    css={css({
                        "div:first-of-type, a:first-of-type, [data-feature-content]":
                            {
                                display: "inline",
                                marginInlineEnd: "4px"
                            }
                    })}
                >
                    {featureCellContent}
                    {props.lozengeText && (
                        <Lozenge>{props.lozengeText}</Lozenge>
                    )}
                </span>
                <FeatureQuestionMark />
            </td>
            {props.tierCellsContent.map((content, index) => (
                <FeaturesTableCell
                    key={index}
                    cellContent={content}
                    index={index}
                />
            ))}
        </tr>
    );
};

export const FeaturesTableCell = ({
    cellContent,
    index
}: {
    cellContent: TierCellContent;
    index: number;
}) => {
    const intl = useIntl();

    return (
        <td css={tdStyles}>
            <span
                css={css({
                    display: "flex",
                    ["@media (min-width: 768px)"]: {
                        display: "none"
                    }
                })}
            >
                {tiers(intl)[index]}
            </span>
            {cellContent &&
                (cellContent === true ? (
                    <Checkmark />
                ) : (
                    <span css={css({ width: "50%", textAlign: "start" })}>
                        {cellContent}
                    </span>
                ))}
        </td>
    );
};

export const FeatureQuestionMark = () => {
    return (
        <span
            css={css({
                backgroundColor: "#5e6c84",
                borderRadius: "50%",
                color: "#fff",
                display: "inline-block",
                flexShrink: 0,
                lineHeight: "20px",
                margin: "2px",
                textAlign: "center",
                height: "20px",
                width: "20px",
                ["@media (min-width: 768px)"]: {
                    display: "none"
                }
            })}
        >
            ?
        </span>
    );
};

export const Checkmark = () => {
    return (
        <span
            css={css({
                backgroundColor: "#00875a",
                borderRadius: "50%",
                color: "#fff",
                display: "inline-block",
                flexShrink: 0,
                fontWeight: "bold",
                lineHeight: "24px",
                margin: "4px",
                textAlign: "center",
                height: "24px",
                width: "24px"
            })}
        >
            ✓
        </span>
    );
};
